import { useEffect, useRef, useState } from 'react'
import { Hand, MicOff, MonitorUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useAudioLevel } from '@/lib/meetings/audio-level'
import { attachVideo, participantInitials, type TileParticipant } from '@/lib/meetings/rtk'

/**
 * One video tile (PLAN-085 §1.5). Remote audio is **not** played here: the
 * session provider owns one hidden `<audio>` per participant (board fqw7),
 * so the meeting stays audible on every route and is never played twice.
 *
 * Speaking (board m5gc #21): the SDK only offers a single "active speaker"
 * event (`participants.activeSpeaker`, one peer id, no level), so each
 * remote tile runs its own AnalyserNode on the participant's audio track
 * with hysteresis; your own tile gets the same from the mic meter. The SDK's
 * active speaker still counts as a fallback.
 */
export function ParticipantTile({
  participant,
  speaking,
  handRaised = false,
  avatarUrl = null,
  className,
  showScreenShare = false,
}: {
  participant: TileParticipant
  speaking?: boolean
  /** Their hand is up (board m5gc #12). */
  handRaised?: boolean
  /** Shown while the camera is off (board m5gc #13); initials otherwise. */
  avatarUrl?: string | null
  className?: string
  showScreenShare?: boolean
}) {
  const { t } = useTranslation('meet')
  const videoRef = useRef<HTMLVideoElement>(null)

  const track = showScreenShare ? participant.screenShareTrack : participant.videoTrack
  const showVideo = showScreenShare ? participant.screenShareEnabled : participant.videoEnabled

  const [heard, setHeard] = useState(false)
  useAudioLevel(
    participant.audioTrack,
    !participant.isSelf && !showScreenShare && participant.audioEnabled,
    { onSpeaking: setHeard }
  )
  // The analyser is the truth when it can run; the SDK's "last active
  // speaker" (which never clears) only stands in when there is no track to listen to.
  const isSpeaking = participant.isSelf
    ? Boolean(speaking)
    : heard || (Boolean(speaking) && !participant.audioTrack)

  useEffect(() => {
    if (videoRef.current) attachVideo(videoRef.current, showVideo ? track : null)
  }, [track, showVideo])

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg bg-zinc-900 text-white',
        isSpeaking && 'ring-2 ring-emerald-400',
        className
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          // A shared screen is letterboxed, never cropped (board m5gc #22).
          showScreenShare ? 'h-full w-full bg-black object-contain' : 'h-full w-full object-cover',
          // Your own camera reads like a mirror, as every meeting app does.
          participant.isSelf && !showScreenShare && '-scale-x-100',
          !showVideo && 'hidden'
        )}
      />
      {!showVideo && (
        <div className="flex h-full w-full items-center justify-center">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              data-testid="tile-avatar"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-zinc-700 sm:h-14 sm:w-14"
            />
          ) : (
            <span className="text-sm font-semibold tracking-wide">
              {participantInitials(participant)}
            </span>
          )}
        </div>
      )}

      {isSpeaking && !showScreenShare && (
        <span
          data-testid="speaking-dot"
          className="absolute top-1.5 left-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/90 shadow"
          aria-label={t('dock.speaking')}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-white" />
        </span>
      )}

      {handRaised && !showScreenShare && (
        <span
          data-testid="hand-badge"
          title={t('dock.handRaised')}
          className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-black shadow"
        >
          <Hand className="h-3.5 w-3.5" aria-label={t('dock.handRaised')} />
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
        {!participant.audioEnabled && !showScreenShare && (
          <MicOff className="h-3 w-3 shrink-0" aria-label={t('dock.muted')} />
        )}
        {showScreenShare && <MonitorUp className="h-3 w-3 shrink-0" />}
        <span className="truncate text-[10px] font-medium">
          {participant.isSelf ? t('dock.you') : participant.name}
        </span>
      </div>
    </div>
  )
}
